import { createUrl } from "@/redux/api";
import {
  AdminKnowledgeCheck,
  AdminKnowledgeCheckAttempt,
  AdminKnowledgeCheckAttemptListItem,
  AnalyticsPeriodQueryParams,
  AnalyticsQueryParams,
  CertificationDetail,
  CertificationInvitation,
  CertificationInvitationQueryParams,
  CertificationListItem,
  CertificationProgress,
  CertificationQueryParams,
  CertificationStats,
  DownloadResourcesRequest,
  CommunityAnalytics,
  EndPlaybackSessionRequest,
  EnrollmentResponse,
  EnrollRequest,
  HeartbeatRequest,
  HeartbeatResponse,
  KnowledgeCheck,
  KnowledgeCheckAttempt,
  KnowledgeCheckAnalytics,
  KnowledgeCheckAttemptQueryParams,
  ModulesAnalytics,
  ProgressionAnalytics,
  TimelineAnalytics,
  TracksAnalyticsQueryParams,
  Participant,
  ParticipantExportParams,
  ParticipantQueryParams,
  PlaybackSessionRequest,
  PlaybackSnapshot,
  PlaybackUrl,
  InviteParticipantsRequest,
  ResendInvitationRequest,
  ResourceDownload,
  ReviewAttemptRequest,
  StartedPlaybackSession,
  StartPlaybackSessionRequest,
  SubmitAttemptRequest,
  SwitchTrackRequest,
  Tags,
  TagsType,
  TracksAnalytics,
  AdminContributionGrade,
  ContributionGrade,
  ContributionGradeQueryParams,
  ReviewContributionRequest,
} from "@/redux/certifications/types";
import { BuilderType, PaginatedResponse } from "@/redux/types";

const certificationTags = (slug: string): TagsType[] => [
  { type: "Certification", id: slug },
  { type: "Certifications" },
  { type: "CertificationProgress", id: slug },
];

const certificationsService = (builder: BuilderType<Tags>) => ({
  getCertifications: builder.query<
    CertificationListItem[] | PaginatedResponse<CertificationListItem>,
    CertificationQueryParams | void
  >({
    query: (filters) => ({
      url: createUrl("/certifications/", filters),
      method: "GET",
    }),
    providesTags: (): TagsType[] => [{ type: "Certifications" }],
  }),
  getCertification: builder.query<CertificationDetail, string>({
    query: (typeKey) => ({
      url: `/certifications/current/${typeKey}/`,
      method: "GET",
    }),
    providesTags: (result, _error, typeKey): TagsType[] => [
      { type: "Certification", id: result?.slug ?? typeKey },
    ],
  }),
  getCertificationProgress: builder.query<CertificationProgress, string>({
    query: (slug) => ({
      url: `/certifications/${slug}/progress/`,
      method: "GET",
    }),
    providesTags: (_result, _error, slug): TagsType[] => [
      { type: "CertificationProgress", id: slug },
    ],
  }),
  enroll: builder.mutation<EnrollmentResponse, EnrollRequest>({
    query: (data) => ({
      url: "/certifications/enrollments/",
      method: "POST",
      data,
    }),
    invalidatesTags: (_result, error, { certificationSlug }): TagsType[] =>
      error ? [] : certificationTags(certificationSlug),
  }),
  switchTrack: builder.mutation<EnrollmentResponse, SwitchTrackRequest>({
    query: ({ enrollmentId, trackId }) => ({
      url: `/certifications/enrollments/${enrollmentId}/track/`,
      method: "PATCH",
      data: { trackId },
    }),
    invalidatesTags: (_result, error, { certificationSlug }): TagsType[] =>
      error ? [] : certificationTags(certificationSlug),
  }),
  getParticipants: builder.query<
    Participant[] | PaginatedResponse<Participant>,
    ParticipantQueryParams
  >({
    query: (filters) => ({
      url: createUrl("/certifications/enrollments/", filters),
      method: "GET",
    }),
    providesTags: (): TagsType[] => [{ type: "Participants" }],
  }),
  getEnrollment: builder.query<EnrollmentResponse | Participant, number>({
    query: (id) => ({
      url: `/certifications/enrollments/${id}/`,
      method: "GET",
    }),
    providesTags: (_result, _error, id): TagsType[] => [
      { type: "Enrollment", id },
    ],
  }),
  withdrawEnrollment: builder.mutation<EnrollmentResponse, number>({
    query: (id) => ({
      url: `/certifications/enrollments/${id}/withdraw/`,
      method: "POST",
    }),
    invalidatesTags: (_result, error): TagsType[] =>
      error
        ? []
        : [
            { type: "Participants" },
            { type: "CertificationStats" },
            { type: "CertificationAnalytics" },
          ],
  }),
  reinstateEnrollment: builder.mutation<EnrollmentResponse, number>({
    query: (id) => ({
      url: `/certifications/enrollments/${id}/reinstate/`,
      method: "POST",
    }),
    invalidatesTags: (_result, error): TagsType[] =>
      error
        ? []
        : [
            { type: "Participants" },
            { type: "CertificationStats" },
            { type: "CertificationAnalytics" },
          ],
  }),
  getParticipantProgress: builder.query<CertificationProgress, number>({
    query: (enrollmentId) => ({
      url: `/certifications/enrollments/${enrollmentId}/progress/`,
      method: "GET",
    }),
    providesTags: (_result, _error, enrollmentId): TagsType[] => [
      { type: "ParticipantProgress", id: enrollmentId },
    ],
  }),
  exportParticipants: builder.mutation<string, ParticipantExportParams>({
    query: (filters) => ({
      url: createUrl("/certifications/enrollments/export/", filters),
      method: "GET",
      responseType: "text",
    }),
  }),
  getCertificationStats: builder.query<CertificationStats, string>({
    query: (slug) => ({
      url: `/certifications/${slug}/stats/`,
      method: "GET",
    }),
    providesTags: (_result, _error, slug): TagsType[] => [
      { type: "CertificationStats", id: slug },
    ],
  }),
  getContributionGrades: builder.query<
    ContributionGrade[] | PaginatedResponse<ContributionGrade>,
    ContributionGradeQueryParams
  >({
    query: (filters) => ({
      url: createUrl("/certifications/contribution-grades/", filters),
      method: "GET",
    }),
    providesTags: (): TagsType[] => [{ type: "ContributionGrades" }],
  }),
  getAdminContributionGrades: builder.query<
    AdminContributionGrade[] | PaginatedResponse<AdminContributionGrade>,
    ContributionGradeQueryParams
  >({
    query: (filters) => ({
      url: createUrl("/certifications/contribution-grades/", filters),
      method: "GET",
    }),
    providesTags: (): TagsType[] => [{ type: "ContributionGrades" }],
  }),
  reviewContribution: builder.mutation<
    AdminContributionGrade,
    ReviewContributionRequest
  >({
    query: ({ gradeId, ...data }) => ({
      url: `/certifications/contribution-grades/${gradeId}/review/`,
      method: "POST",
      data,
    }),
    invalidatesTags: (_result, error): TagsType[] =>
      error
        ? []
        : [
            { type: "ContributionGrades" },
            { type: "Participants" },
            { type: "CertificationStats" },
            { type: "CertificationAnalytics" },
          ],
  }),
  getKnowledgeCheckAttempts: builder.query<
    | AdminKnowledgeCheckAttemptListItem[]
    | PaginatedResponse<AdminKnowledgeCheckAttemptListItem>,
    KnowledgeCheckAttemptQueryParams
  >({
    query: (filters) => ({
      url: createUrl("/certifications/knowledge-check-attempts/", filters),
      method: "GET",
    }),
    providesTags: (): TagsType[] => [{ type: "KnowledgeCheckAttempts" }],
  }),
  getKnowledgeCheckAttempt: builder.query<AdminKnowledgeCheckAttempt, number>({
    query: (id) => ({
      url: `/certifications/knowledge-check-attempts/${id}/`,
      method: "GET",
    }),
    providesTags: (): TagsType[] => [{ type: "KnowledgeCheckAttempts" }],
  }),
  getAdminKnowledgeCheck: builder.query<AdminKnowledgeCheck, number>({
    query: (id) => ({
      url: `/certifications/knowledge-checks/${id}/`,
      method: "GET",
    }),
    providesTags: (_result, _error, id): TagsType[] => [
      { type: "KnowledgeCheck", id },
    ],
  }),
  reviewAttempt: builder.mutation<
    AdminKnowledgeCheckAttempt,
    ReviewAttemptRequest
  >({
    query: ({ attemptId, ...data }) => ({
      url: `/certifications/knowledge-check-attempts/${attemptId}/review/`,
      method: "POST",
      data,
    }),
    invalidatesTags: (): TagsType[] => [
      { type: "KnowledgeCheckAttempts" },
      { type: "CertificationAnalytics" },
      { type: "CertificationStats" },
    ],
  }),
  downloadModuleResources: builder.mutation<
    ResourceDownload[],
    DownloadResourcesRequest
  >({
    query: ({ moduleId, contentItems }) => ({
      url: `/certifications/modules/${moduleId}/resources/download/`,
      method: "POST",
      data: { contentItems },
    }),
    invalidatesTags: (_result, error, { certificationSlug }): TagsType[] =>
      error ? [] : [{ type: "CertificationProgress", id: certificationSlug }],
  }),
  getModulesAnalytics: builder.query<ModulesAnalytics, AnalyticsQueryParams>({
    query: ({ certificationSlug, ...filters }) => ({
      url: createUrl(
        `/certifications/${certificationSlug}/analytics/modules/`,
        filters,
      ),
      method: "GET",
    }),
    providesTags: (_result, _error, { certificationSlug }): TagsType[] => [
      { type: "CertificationAnalytics", id: certificationSlug },
    ],
  }),
  getCommunityAnalytics: builder.query<
    CommunityAnalytics,
    AnalyticsQueryParams
  >({
    query: ({ certificationSlug, ...filters }) => ({
      url: createUrl(
        `/certifications/${certificationSlug}/analytics/community/`,
        filters,
      ),
      method: "GET",
    }),
    providesTags: (_result, _error, { certificationSlug }): TagsType[] => [
      { type: "CertificationAnalytics", id: certificationSlug },
    ],
  }),
  getTracksAnalytics: builder.query<
    TracksAnalytics,
    TracksAnalyticsQueryParams
  >({
    query: ({ certificationSlug }) => ({
      url: `/certifications/${certificationSlug}/analytics/tracks/`,
      method: "GET",
    }),
    providesTags: (_result, _error, { certificationSlug }): TagsType[] => [
      { type: "CertificationAnalytics", id: certificationSlug },
    ],
  }),
  getTimelineAnalytics: builder.query<
    TimelineAnalytics,
    AnalyticsPeriodQueryParams
  >({
    query: ({ certificationSlug, ...filters }) => ({
      url: createUrl(
        `/certifications/${certificationSlug}/analytics/timeline/`,
        filters,
      ),
      method: "GET",
    }),
    providesTags: (_result, _error, { certificationSlug }): TagsType[] => [
      { type: "CertificationAnalytics", id: certificationSlug },
    ],
  }),
  getProgressionAnalytics: builder.query<
    ProgressionAnalytics,
    AnalyticsPeriodQueryParams
  >({
    query: ({ certificationSlug, ...filters }) => ({
      url: createUrl(
        `/certifications/${certificationSlug}/analytics/progression/`,
        filters,
      ),
      method: "GET",
    }),
    providesTags: (_result, _error, { certificationSlug }): TagsType[] => [
      { type: "CertificationAnalytics", id: certificationSlug },
    ],
  }),
  getKnowledgeCheckAnalytics: builder.query<
    KnowledgeCheckAnalytics,
    AnalyticsQueryParams
  >({
    query: ({ certificationSlug, ...filters }) => ({
      url: createUrl(
        `/certifications/${certificationSlug}/analytics/knowledge-checks/`,
        filters,
      ),
      method: "GET",
    }),
    providesTags: (_result, _error, { certificationSlug }): TagsType[] => [
      { type: "CertificationAnalytics", id: certificationSlug },
    ],
  }),
  getCertificationInvitations: builder.query<
    CertificationInvitation[] | PaginatedResponse<CertificationInvitation>,
    CertificationInvitationQueryParams
  >({
    query: (filters) => ({
      url: createUrl("/certifications/invitations/", filters),
      method: "GET",
    }),
    providesTags: (_result, _error, { certificationSlug }): TagsType[] => [
      { type: "CertificationInvitations", id: certificationSlug },
    ],
  }),
  inviteParticipants: builder.mutation<
    CertificationInvitation[],
    InviteParticipantsRequest
  >({
    query: ({ certificationSlug, emails }) => ({
      url: `/certifications/${certificationSlug}/invitations/`,
      method: "POST",
      data: { emails },
    }),
    invalidatesTags: (_result, error, { certificationSlug }): TagsType[] =>
      error
        ? []
        : [
            { type: "CertificationInvitations", id: certificationSlug },
            { type: "CertificationStats", id: certificationSlug },
          ],
  }),
  resendCertificationInvitation: builder.mutation<
    CertificationInvitation,
    ResendInvitationRequest
  >({
    query: ({ invitationId }) => ({
      url: `/certifications/invitations/${invitationId}/resend/`,
      method: "POST",
    }),
    invalidatesTags: (_result, error, { certificationSlug }): TagsType[] =>
      error
        ? []
        : [{ type: "CertificationInvitations", id: certificationSlug }],
  }),
  startPlaybackSession: builder.mutation<
    StartedPlaybackSession,
    StartPlaybackSessionRequest
  >({
    query: (data) => ({
      url: "/certifications/playback-sessions/",
      method: "POST",
      data,
    }),
  }),
  sendHeartbeat: builder.mutation<HeartbeatResponse, HeartbeatRequest>({
    query: ({ sessionId, ...data }) => ({
      url: `/certifications/playback-sessions/${sessionId}/heartbeat/`,
      method: "POST",
      data,
    }),
  }),
  refreshPlaybackUrl: builder.mutation<PlaybackUrl, PlaybackSessionRequest>({
    query: ({ sessionId }) => ({
      url: `/certifications/playback-sessions/${sessionId}/refresh-url/`,
      method: "POST",
    }),
  }),
  endPlaybackSession: builder.mutation<
    PlaybackSnapshot,
    EndPlaybackSessionRequest
  >({
    query: ({ sessionId, lastPositionSeconds }) => ({
      url: `/certifications/playback-sessions/${sessionId}/end/`,
      method: "POST",
      data: { lastPositionSeconds },
    }),
    invalidatesTags: (_result, error, { certificationSlug }): TagsType[] =>
      error ? [] : [{ type: "CertificationProgress", id: certificationSlug }],
  }),
  getKnowledgeCheck: builder.query<KnowledgeCheck, number>({
    query: (id) => ({
      url: `/certifications/knowledge-checks/${id}/`,
      method: "GET",
    }),
    providesTags: (_result, _error, id): TagsType[] => [
      { type: "KnowledgeCheck", id },
    ],
  }),
  submitAttempt: builder.mutation<KnowledgeCheckAttempt, SubmitAttemptRequest>({
    query: ({ knowledgeCheckId, answers }) => ({
      url: "/certifications/knowledge-check-attempts/",
      method: "POST",
      data: { knowledgeCheckId, answers },
    }),
    invalidatesTags: (
      _result,
      error,
      { certificationSlug, knowledgeCheckId },
    ): TagsType[] => {
      if (!error) {
        return [
          { type: "KnowledgeCheck", id: knowledgeCheckId },
          { type: "CertificationProgress", id: certificationSlug },
        ];
      }
      return "status" in error && error.status === 409
        ? [{ type: "KnowledgeCheck", id: knowledgeCheckId }]
        : [];
    },
  }),
});

export default certificationsService;
